/* tslint:disable */
/* eslint-disable */
import { gql } from "@apollo/client";

/**
 * Lean participant-case query used app-wide (App, ProtectedRoute, Dashboard,
 * OfficerProfile, Documents). Carries no Question Forms fields, so it is safe to
 * run in any environment regardless of whether the question-forms schema is
 * deployed. This is the default; the wizard path opts into the expanded query
 * below behind the `question-forms-participant-webapp` flag.
 */
export const GET_PARTICIPANT_CASE = gql`
  query GetParticipantCase {
    getParticipantCase {
      caseId
      onboardingCompletedAt
      participant {
        id
        firstName
        lastName
        __typename
      }
      currentOfficer {
        id
        firstName
        lastName
        active
        phone
        email
        __typename
      }
      nextQuestions {
        notAvailableReason
        time
        __typename
      }
      questionAssignment {
        questionGroupId
        agencyQuestionGroup {
          id
          localizedParticipantQuestions {
            id
            localization {
              text
              locale
              __typename
            }
            __typename
          }
          __typename
        }
        __typename
      }
      pendingQuestions {
        startTime
        answersValidUntil
        promptId
        __typename
      }
      __typename
    }
  }
`;

/**
 * Chat identity fields for {@link GET_PARTICIPANT_CASE}, split into their own
 * document for the same deploy-order reason question-forms fields are: the lean
 * query must stay valid against environments where the chat schema fields are
 * not deployed yet. Only issued when `participant-chat-webapp` is on — which is
 * only enabled in environments where the schema change is deployed — so the
 * app-wide boot path never depends on these fields.
 */
export const GET_PARTICIPANT_CASE_CHAT = gql`
  query GetParticipantCaseChat {
    getParticipantCase {
      caseId
      chatChannelId
      chatUserId
      __typename
    }
  }
`;

/**
 * Question Forms variant of {@link GET_PARTICIPANT_CASE}: same shape plus the
 * question-type / workflow-name / semantic-group / prepopulation fields the
 * wizard needs. Takes a `GetParticipantCaseInput` so the caller can request
 * `supportedQuestionTypes` (so text/workflow questions aren't bool-filtered) and
 * `loadQuestionPrepopulation`. Workflow labels resolve server-first from
 * `workflowDefinition` (agency/library label overrides), falling back to the
 * client label catalog where the server has none (IHA-383).
 *
 * Only issued when `question-forms-participant-webapp` is on — which is only
 * enabled in environments where the question-forms schema is deployed — so the
 * app-wide boot path never depends on (or over-fetches) these fields when the
 * feature is off.
 */
export const GET_PARTICIPANT_CASE_WITH_QUESTION_FORMS = gql`
  query GetParticipantCaseWithQuestionForms($input: GetParticipantCaseInput) {
    getParticipantCase(input: $input) {
      caseId
      onboardingCompletedAt
      participant {
        id
        firstName
        lastName
        __typename
      }
      currentOfficer {
        id
        firstName
        lastName
        active
        phone
        email
        __typename
      }
      nextQuestions {
        notAvailableReason
        time
        __typename
      }
      questionAssignment {
        questionGroupId
        agencyQuestionGroup {
          id
          displaySectionGroupNames
          localizedParticipantQuestions {
            id
            localization {
              text
              locale
              __typename
            }
            questionType
            workflowName
            semanticGroup {
              key
              localization {
                locale
                text
                __typename
              }
              __typename
            }
            workflowDefinition {
              steps {
                stepId
                labels {
                  key
                  value {
                    locale
                    text
                    __typename
                  }
                  __typename
                }
                __typename
              }
              __typename
            }
            __typename
          }
          __typename
        }
        __typename
      }
      pendingQuestions {
        startTime
        answersValidUntil
        promptId
        __typename
      }
      questionPrepopulation {
        workflowName
        values
        __typename
      }
      __typename
    }
  }
`;
